import {
  bindings,
  sync,
  syncAll,
  insertSlides,
  insertJoinInstructions,
  updatePlaceholders,
  insertFlowSlides,
  clearResponses
} from '@polleverywhere/slideware-bindings'
import fetchActivity from '../../util/fetch_activity'
import ErrorModel from '../../models/error'
import totalSlideCount from '../../util/total_slide_count'
import store from '@polleverywhere/redux'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import User from '@polleverywhere/core/lib/models/user'

function finishAction (page, options = { }) {
  this.set({
    page,
    pageAction: null
  }, options)
}

function finishWithError (error) {
  this.set({
    page: 'error',
    pageAction: null,
    error
  })
}

function areSlidesInsertable () {
  // README: Avoid calling this at the top of the action().
  // We want to avoid calling the binding layer as much as possible.
  let insertable
  try {
    insertable = bindings.slidesInsertable()
  } catch (ex) {
    console.err('intentionally catching error for background route rollbars')
  }
  return insertable || false
}

export default class State extends Backbone.Model {
  get defaults () {
    return {
      page: null, // sync | syncAll | insert | support | edit
      pageAction: null, // retry
      activity: null,
      // Goog_presenter shows 'inserting' UI, which is indicated by a progress bar.
      // In the future, we are going to remove this an send inserting progress back. (Pivotal: #166997437)
      showInsertComplete: true,
      insertionType: 'activities', // activities | polls | surveys | images (slideware-bindings:insertSlides)
      mode: 'standalone' // standalone | integration
    }
  }

  initialize (attrs = {}, options = {}) {
    this.on('change:page', (model, page, options = {}) => this.action(model, page, options))
    this.on('change:pageAction', (model, page, options = {}) => {
      switch (model.get('pageAction')) {
        case 'retry':
          model.set('pageAction', null)
          this.action(model, model.get('page'), options)
          break
        default:
      }
    })
  }

  async action (model, page, options = {}) {
    switch (page) {
      case 'background':
        try {
          if (profileSlice.selectIsLoggedIn(store.getState())) {
            updatePlaceholders(new User(store.getState()[profileSlice.name].user))
          }
        } catch (message) {
          // The background is only visible to devs. This would only be
          // meaningful to us.
          finishWithError.call(this, new ErrorModel({ code: 'Background error', message }))
        }
        break

      case 'sync':
        try {
          if (!await areSlidesInsertable()) { return }
          let result = await fetchActivity(model.get('activity'))
          if (result instanceof ErrorModel) {
            throw new Error(result.get('xhr').responseJSON.error.message)
          }
          result = [result].flat()
          result && result[0] && await sync(result[0])
          finishAction.call(this, 'close')
        } catch (message) {
          finishWithError.call(this, new ErrorModel({ code: 'Sync error', message }))
        }
        break

      case 'syncAll':
        try {
          if (!await areSlidesInsertable()) { return }
          await syncAll()
          finishAction.call(this, 'close')
        } catch (err) {
          const message = err && err.responseJSON && err.responseJSON.error ? err.responseJSON.error.message : err
          finishWithError.call(this, new ErrorModel({ code: 'Sync all error', message }))
        }
        break

      case 'insertJoinInstructions':
        try {
          if (!await areSlidesInsertable()) { return }
          await insertJoinInstructions()
          finishAction.call(this, 'done', { slideCount: 1 })
        } catch (err) {
          const message = err && err.responseJSON && err.responseJSON.error ? err.responseJSON.error.message : err
          finishWithError.call(this, new ErrorModel({ code: 'Insert join instructions error', message }))
        }
        break

      case 'insert':
        try {
          if (!await areSlidesInsertable()) { return }
          const currentActivities = [model.get('activity')].flat()
          await Promise.all(currentActivities.map(activity => fetchActivity(activity, {
            include: 'poll_instances',
            exclude: 'options,results,flash_options,visual_settings'
          })))

          if (model.get('slidesets')?.length) {
            await insertFlowSlides(model.get('slidesets'))
            model.set('slidesets', null)
          } else {
            await insertSlides(currentActivities, {
              insertionType: model.get('insertionType')
            })
          }

          // We aren't actually done inserting. We have only finished downloading the images from the server and passed
          // the info to slideware to insert.
          // Goog_presenter shows 'inserting' UI, which is indicated by a progress bar.
          // In the future, we are going to remove this an send inserting progress back. (Pivotal: #166997437)
          if (!model.get('showInsertComplete')) {
            bindings.closeWindow()
            return
          }

          const slideCount = totalSlideCount(currentActivities)
          finishAction.call(this, 'done', { slideCount })
        } catch (message) {
          model.set('slidesets', null)
          finishWithError.call(this, new ErrorModel({ code: 'Insert slides error', message }))
        }
        break

      case 'clearResponses':
        try {
          if (!await areSlidesInsertable()) { return }
          await clearResponses(model.get('activity'), options.archiveName)
          finishAction.call(this, 'clearResponsesDone')
        } catch (message) {
          finishWithError.call(this, new ErrorModel({ code: 'Clear responses error', message }))
        }
        break

      default: break
    }
  }

  inspect () {
    return super.toJSON()
  }
}
